import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MdApartment,
  MdCalendarToday,
  MdAttachMoney,
  MdEventAvailable,
  MdInfoOutline,
  MdRefresh,
  MdChat,
} from 'react-icons/md';
import { getMyBnbBookings } from '../../../api/services/propertyService';
import CollapsibleTopPanel from '../../../components/CollapsibleTopPanel/CollapsibleTopPanel';
import GuestChatModal from '../../shared/GuestChatModal/GuestChatModal';
import './BnbBookings.css';

const PHASE_FILTERS = [
  { value: 'all', label: 'All stays' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'current', label: 'Current stay' },
  { value: 'past', label: 'Past' },
];

function formatMoneyKES(amount) {
  const value = Number(amount);
  if (!Number.isFinite(value)) return null;
  return `KES ${value.toLocaleString()}`;
}

function formatDate(isoString) {
  if (!isoString) return '—';
  const d = new Date(isoString);
  if (Number.isNaN(d.getTime())) return isoString;
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function startOfDay(value) {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  d.setHours(0, 0, 0, 0);
  return d;
}

function getStayPhase(booking) {
  const status = String(booking?.status || '').toUpperCase();
  if (status === 'CANCELLED' || status === 'REJECTED') return 'past';

  const today = startOfDay(new Date());
  const checkIn = booking?.checkInDate ? startOfDay(booking.checkInDate) : null;
  const checkOut = booking?.checkOutDate ? startOfDay(booking.checkOutDate) : null;

  if (checkIn && checkOut) {
    if (today >= checkIn && today <= checkOut) return 'current';
    if (checkIn > today) return 'upcoming';
    if (checkOut < today) return 'past';
  }

  if (checkIn && checkIn > today) return 'upcoming';
  if (checkOut && checkOut < today) return 'past';

  if (['PENDING', 'APPROVED', 'REQUESTED'].includes(status)) return 'upcoming';
  if (status === 'CONFIRMED' || status === 'CHECKED_IN') return 'current';

  return 'past';
}

function phaseLabel(phase) {
  if (phase === 'upcoming') return 'Upcoming';
  if (phase === 'current') return 'Current stay';
  if (phase === 'past') return 'Past stay';
  return 'Stay';
}

function parseBookingsResponse(res) {
  const payload = res?.data ?? res;
  const list = Array.isArray(payload) ? payload : payload?.data ?? [];
  return Array.isArray(list) ? list : [];
}

function getBookingPropertyId(booking) {
  if (!booking) return null;
  return booking.propertyId || booking.propertyID || booking.property?.id || null;
}

const BnbBookings = () => {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [phaseFilter, setPhaseFilter] = useState('all');
  const [chatBooking, setChatBooking] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getMyBnbBookings();
      const list = parseBookingsResponse(res)
        .map((row) => ({
          ...row,
          id: row.id || row.bookingId,
          stayPhase: getStayPhase(row),
        }))
        .sort((a, b) => {
          const aIn = new Date(a.checkInDate || a.createdAt || 0).getTime();
          const bIn = new Date(b.checkInDate || b.createdAt || 0).getTime();
          return (Number.isFinite(bIn) ? bIn : 0) - (Number.isFinite(aIn) ? aIn : 0);
        });
      setBookings(list);
    } catch (e) {
      setBookings([]);
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filteredBookings = useMemo(() => {
    if (phaseFilter === 'all') return bookings;
    return bookings.filter((row) => row.stayPhase === phaseFilter);
  }, [bookings, phaseFilter]);

  const summary = useMemo(() => {
    const upcoming = bookings.filter((b) => b.stayPhase === 'upcoming').length;
    const current = bookings.filter((b) => b.stayPhase === 'current').length;
    const past = bookings.filter((b) => b.stayPhase === 'past').length;
    return { total: bookings.length, upcoming, current, past };
  }, [bookings]);

  const emptyMessage = useMemo(() => {
    if (phaseFilter === 'upcoming') return 'No upcoming BnB stays yet.';
    if (phaseFilter === 'current') return 'You have no active stay right now.';
    if (phaseFilter === 'past') return 'No past BnB stays to show.';
    return 'You have no BnB bookings yet.';
  }, [phaseFilter]);

  return (
    <div className="bnb-bookings-page">
      <CollapsibleTopPanel id="tenant-bnb-bookings-top" defaultOpenOnMobile={false}>
        <header className="bnb-bookings-header">
          <div className="bnb-bookings-title-block">
            <h1 className="bnb-bookings-title">Booking requests</h1>
            <p className="bnb-bookings-subtitle">
              Upcoming, current, and past BnB stays in one place
            </p>
          </div>
          <button
            type="button"
            className="bnb-bookings-refresh"
            onClick={load}
            disabled={loading}
            aria-label="Refresh"
          >
            <MdRefresh className={loading ? 'spin' : ''} />
          </button>
        </header>

        {!loading && !error && bookings.length > 0 && (
          <div className="bnb-bookings-toolbar">
            <div className="bnb-bookings-filter-group">
              <label htmlFor="bnb-bookings-phase">Show</label>
              <select
                id="bnb-bookings-phase"
                className="bnb-bookings-filter-select"
                value={phaseFilter}
                onChange={(e) => setPhaseFilter(e.target.value)}
                disabled={loading}
              >
                {PHASE_FILTERS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </CollapsibleTopPanel>

      {!loading && !error && bookings.length > 0 && (
        <div className="bnb-bookings-summary-bar" aria-live="polite">
          <span>
            <strong>{summary.total}</strong> booking{summary.total === 1 ? '' : 's'}
          </span>
          {summary.current > 0 && (
            <span className="bnb-bookings-summary-pill bnb-bookings-summary-pill--current">
              <strong>{summary.current}</strong> current
            </span>
          )}
          {summary.upcoming > 0 && (
            <span className="bnb-bookings-summary-pill bnb-bookings-summary-pill--upcoming">
              <strong>{summary.upcoming}</strong> upcoming
            </span>
          )}
          {summary.past > 0 && (
            <span className="bnb-bookings-summary-pill bnb-bookings-summary-pill--past">
              <strong>{summary.past}</strong> past
            </span>
          )}
        </div>
      )}

      {loading && (
        <div className="bnb-bookings-state">
          <div className="bnb-bookings-skeleton wide" />
          <div className="bnb-bookings-skeleton" />
          <div className="bnb-bookings-skeleton" />
        </div>
      )}

      {!loading && error && (
        <div className="bnb-bookings-error" role="alert">
          <div className="bnb-bookings-error-title">Could not load bookings</div>
          <p className="bnb-bookings-error-msg">{error.message || 'Something went wrong.'}</p>
          <button type="button" className="bnb-bookings-retry" onClick={load}>
            Try again
          </button>
        </div>
      )}

      {!loading && !error && bookings.length === 0 && (
        <div className="bnb-bookings-empty">
          <div className="bnb-bookings-empty-icon">
            <MdEventAvailable />
          </div>
          <div className="bnb-bookings-empty-title">No BnB bookings yet</div>
          <p className="bnb-bookings-empty-text">
            When you book a short stay, your upcoming, active, and past visits will appear here.
          </p>
          <button type="button" className="bnb-bookings-browse" onClick={() => navigate('/tenant/listings')}>
            Browse listings
          </button>
        </div>
      )}

      {!loading && !error && bookings.length > 0 && filteredBookings.length === 0 && (
        <div className="bnb-bookings-empty">
          <div className="bnb-bookings-empty-icon">
            <MdEventAvailable />
          </div>
          <div className="bnb-bookings-empty-title">{emptyMessage}</div>
          <p className="bnb-bookings-empty-text">Try another filter to see your other stays.</p>
        </div>
      )}

      {!loading && !error && filteredBookings.length > 0 && (
        <div className="bnb-bookings-list" role="list">
          {filteredBookings.map((booking) => (
            <article key={booking.id} className="bnb-booking-card" role="listitem">
              <div className="bnb-booking-card-top">
                <div className="bnb-booking-card-titles">
                  <h2 className="bnb-booking-property">{booking.propertyName || 'Property'}</h2>
                  <p className="bnb-booking-unit">
                    <MdApartment aria-hidden />
                    {booking.unitName || booking.unitNumber || 'Unit'}
                  </p>
                </div>
                <div className="bnb-booking-badges">
                  <span className={`bnb-booking-phase bnb-booking-phase--${booking.stayPhase}`}>
                    {phaseLabel(booking.stayPhase)}
                  </span>
                  <span className={`bnb-booking-status bnb-booking-status--${String(booking.status || '').toLowerCase()}`}>
                    {booking.status || '—'}
                  </span>
                </div>
              </div>

              <dl className="bnb-booking-dl">
                <div className="bnb-booking-dl-row">
                  <dt>
                    <MdCalendarToday aria-hidden />
                    Check-in
                  </dt>
                  <dd>{formatDate(booking.checkInDate)}</dd>
                </div>
                <div className="bnb-booking-dl-row">
                  <dt>
                    <MdCalendarToday aria-hidden />
                    Check-out
                  </dt>
                  <dd>{formatDate(booking.checkOutDate)}</dd>
                </div>
                <div className="bnb-booking-dl-row">
                  <dt>
                    <MdAttachMoney aria-hidden />
                    Total
                  </dt>
                  <dd>{formatMoneyKES(booking.totalAmount) || formatMoneyKES(booking.pricePerNight) || '—'}</dd>
                </div>
              </dl>

              {booking.bookingCode ? (
                <div className="bnb-booking-code">
                  <MdInfoOutline aria-hidden />
                  <span>Booking code: {booking.bookingCode}</span>
                </div>
              ) : null}

              {booking.stayPhase === 'current' && (
                <div className="bnb-booking-highlight" role="status">
                  You are currently checked in for this stay.
                </div>
              )}

              {getBookingPropertyId(booking) ? (
                <div className="bnb-booking-actions">
                  <button
                    type="button"
                    className="bnb-booking-chat-btn"
                    onClick={() => setChatBooking({ ...booking, propertyId: getBookingPropertyId(booking) })}
                  >
                    <MdChat aria-hidden />
                    Chat with Host
                  </button>
                </div>
              ) : null}
            </article>
          ))}
        </div>
      )}

      {!loading && !error && filteredBookings.length > 0 && (
        <div className="bnb-bookings-count">
          {filteredBookings.length} {filteredBookings.length === 1 ? 'booking' : 'bookings'}
          {phaseFilter !== 'all' && ` (${PHASE_FILTERS.find((f) => f.value === phaseFilter)?.label})`}
        </div>
      )}

      <GuestChatModal
        isOpen={Boolean(chatBooking?.propertyId)}
        onClose={() => setChatBooking(null)}
        propertyId={chatBooking?.propertyId}
        guestName={chatBooking?.hostName || chatBooking?.propertyOwnerName || 'Host'}
        propertySubtitle={chatBooking?.propertyName || ''}
        chatMode="bnb"
      />
    </div>
  );
};

export default BnbBookings;
